// Pulley: two sides (lid, bucket or shelf) at either end of a rope over a wheel. The heavier side goes down.
import { defineObj } from '../core/registry';
import { block, rr, lg, shadowOn, shadowOff, C } from '../core/draw-helpers';
import type { Ctx2D, GameBody, PhysicsApi, Vec } from '../core/types';
import { drawStone } from './stone';

type SideType = 'gate' | 'bucket' | 'shelf';
export interface PulleySide { bs: GameBody[]; off: Vec[]; x: number; y0: number; type: SideType | string; h: number }
export interface Pulley { x: number; y: number; A: PulleySide; B: PulleySide; s: number; v: number; min: number; max: number; base: number }
declare module '../core/types' { interface SimState { pulleys: Pulley[] } }
interface SideData { x: number; y: number; type?: string; h?: number }

type SideArt = { type: string; h?: number; bs: { position: Vec }[] };
export function drawPulley(ctx: Ctx2D, P: { x: number; y: number; s: number; A: SideArt; B: SideArt }) {
  const wx = P.x, wy = P.y, R = 26;
  const a = P.A.bs[0].position, b = P.B.bs[0].position;
  const aTop = P.A.type === 'gate' ? a.y - P.A.h! / 2 : a.y - 56;
  const bTop = P.B.type === 'gate' ? b.y - P.B.h! / 2 : b.y - 56;
  ctx.save();
  ctx.strokeStyle = '#C58B4A'; ctx.lineWidth = 4; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(a.x, aTop); ctx.lineTo(wx - R, wy); ctx.arc(wx, wy, R, Math.PI, 0); ctx.lineTo(b.x, bTop); ctx.stroke();
  // wheel
  ctx.fillStyle = C.ink; rr(ctx, wx - 6, wy - 60, 12, 60, 5); ctx.fill();
  shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.arc(wx, wy, R, 0, 7); ctx.fillStyle = lg(ctx, wy - R, wy + R, '#FFD36B', '#F29F16'); ctx.fill(); shadowOff(ctx);
  ctx.save(); ctx.translate(wx, wy); ctx.rotate(P.s / R);
  ctx.strokeStyle = 'rgba(120,70,0,.6)'; ctx.lineWidth = 3;
  for (let i = 0; i < 3; i++) { ctx.rotate(Math.PI / 3); ctx.beginPath(); ctx.moveTo(-R + 6, 0); ctx.lineTo(R - 6, 0); ctx.stroke(); }
  ctx.restore();
  ctx.beginPath(); ctx.arc(wx, wy, 6, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
  // sides
  for (const side of [P.A, P.B]) {
    const p0 = side.bs[0].position;
    if (side.type === 'gate') {
      block(ctx, p0.x, p0.y, 22, side.h!, 0, '#FFB86B', C.wood3, 6);
      ctx.fillStyle = 'rgba(120,60,20,.5)'; for (let k = -1; k <= 1; k++) { ctx.beginPath(); ctx.arc(p0.x, p0.y + k * side.h! / 3, 3, 0, 7); ctx.fill(); }
    } else if (side.type === 'bucket') {
      ctx.save(); shadowOn(ctx, 10, 5);
      ctx.beginPath(); ctx.moveTo(p0.x - 50, p0.y - 56); ctx.lineTo(p0.x + 50, p0.y - 56); ctx.lineTo(p0.x + 42, p0.y + 6); ctx.lineTo(p0.x - 42, p0.y + 6); ctx.closePath();
      ctx.fillStyle = lg(ctx, p0.y - 56, p0.y + 6, 'rgba(160,210,255,.55)', 'rgba(90,150,230,.75)'); ctx.fill(); shadowOff(ctx);
      ctx.strokeStyle = '#4A7FC9'; ctx.lineWidth = 4; ctx.stroke();
      ctx.restore();
    } else block(ctx, p0.x, p0.y, 110, 14, 0, C.wood1, C.wood2, 6);
  }
  ctx.restore();
}

// The weight on top of a side (dynamic bodies touching it from above)
function loadOn(api: PhysicsApi, side: PulleySide) {
  let m = 0;
  const set = new Set(side.bs);
  for (const p of api.engine.pairs.list) {
    if (!p.isActive) continue;
    const A = (p.bodyA.parent || p.bodyA) as GameBody, B = (p.bodyB.parent || p.bodyB) as GameBody;
    let o: GameBody | null = null;
    if (set.has(A) && api.isDynamic(B)) o = B; else if (set.has(B) && api.isDynamic(A)) o = A;
    if (o && o.position.y < side.bs[0].position.y) m += o.mass;
  }
  return m;
}

export const pulley = defineObj({
  phase: 'core', layer: 'back', z: -2, thumb: 1,
  // o: { x (wheel), y (wheel), A:{x,y,type}, B:{x,y,type}, range, min }
  create(api, o) {
    const { add, R } = api;
    const mk = (side: SideData, tag: string): PulleySide => {
      const bs: GameBody[] = [];
      if (side.type === 'gate') bs.push(add(R(side.x, side.y, 22, side.h || 140, { isStatic: true, chamfer: { radius: 5 } })));
      else if (side.type === 'bucket') {
        bs.push(add(R(side.x, side.y, 90, 12, { isStatic: true })));
        bs.push(add(R(side.x - 45, side.y - 26, 10, 60, { isStatic: true })));
        bs.push(add(R(side.x + 45, side.y - 26, 10, 60, { isStatic: true })));
      } else bs.push(add(R(side.x, side.y, 110, 14, { isStatic: true, chamfer: { radius: 5 } })));
      const off = bs.map(b => ({ x: b.position.x - side.x, y: b.position.y - side.y }));
      bs.forEach(b => { b.kind = 'pulley' + side.type; b.pside = tag; });
      return { bs, off, x: side.x, y0: side.y, type: side.type as string, h: side.h || 140 };
    };
    const range = o.range as number;
    const P: Pulley = { x: o.x, y: o.y, A: mk(o.A as SideData, 'A'), B: mk(o.B as SideData, 'B'), s: 0, v: 0, min: o.min != null ? o.min as number : -range, max: range, base: 4 };
    api.S.pulleys.push(P);
    return P;
  },
  draw: (ctx, st) => drawPulley(ctx, st),
  blockers: o => [{ x: o.x, y: o.y, r: 40 }],
  system: {
    init: api => { api.S.pulleys = []; },
    // 1 degree of freedom: s = how much A is raised
    preStep(api) {
      const S = api.S, { Body } = api;
      // Matter 0.20: setPosition(body, position, updateVelocity)
      const setPos = Body.setPosition as (b: GameBody, p: Vec, updateVelocity?: boolean) => void;
      for (const P of S.pulleys) {
        const mA = (P.A.type === 'gate' ? 9 : 4) + (P.A.type === 'gate' ? 0 : loadOn(api, P.A));
        const mB = (P.B.type === 'gate' ? 9 : 4) + (P.B.type === 'gate' ? 0 : loadOn(api, P.B));
        const g = 0.2778 * api.engine.gravity.y;
        P.v += g * (mB - mA) / (mA + mB);
        P.v *= 0.97;
        if (Math.abs(P.v) > 6) P.v = Math.sign(P.v) * 6;
        let ns = P.s + P.v;
        if (ns > P.max) { ns = P.max; P.v = 0; }
        if (ns < P.min) { ns = P.min; P.v = 0; }
        const d = ns - P.s; P.s = ns;
        if (d !== 0) {
          P.A.bs.forEach(b => setPos(b, { x: b.position.x, y: b.position.y - d }, true));
          P.B.bs.forEach(b => setPos(b, { x: b.position.x, y: b.position.y + d }, true));
          if (Math.abs(d) > 0.3 && S.t % 6 === 0) S.events.push({ e: 'creak', x: P.x, y: P.y });
        } else {
          P.A.bs.forEach(b => Body.setVelocity(b, { x: 0, y: 0 }));
          P.B.bs.forEach(b => Body.setVelocity(b, { x: 0, y: 0 }));
        }
      }
    },
    busy: api => api.S.pulleys.some(P => Math.abs(P.v) > 0.05),
  },
  intro(x, t, g, { fakeBall, arrow, ease }) {
    const drop = t < 50 ? 20 + 0.05 * t * t : null;
    const s = ease((t - 50) / 40) * 70;
    const P = { x: 230, y: 50, s, A: { type: 'gate', h: 110, bs: [{ position: { x: 120, y: 175 - s } }] }, B: { type: 'bucket', bs: [{ position: { x: 340, y: 130 + s } }] } };
    drawPulley(x, P);
    drawStone(x, 340, drop != null ? Math.min(drop, 100 + s) : 100 + s, 0);
    const bx = t > 95 ? Math.min(220, 70 + (t - 95) * 2.2) : 70;
    g.ball(x, fakeBall(bx, 222, t > 95 ? 3 : 0, 0), 'hero', t);
    arrow(400, 150, 400, 60); arrow(60, 150, 60, 60, true);
  },
});
